import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Contact = () => {
  const { t } = useTranslation();
  const formRef = useRef();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await emailjs.sendForm(
        'service_7q6p0lq', // Replace with your EmailJS service ID
        'template_cqjbmaj', // Replace with your EmailJS template ID
        formRef.current,
        '4-7m0vaElv_7Fbuf1' // Replace with your EmailJS public key
      );
      
      setSuccess(true);
      formRef.current.reset();
    } catch (error) {
      setError(t('contact.form.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d1345] flex flex-col">
      <Navbar />
      <main className="flex-grow flex items-center justify-center pt-24">
        <section className="w-full py-16">
        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Two Column Header */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
            {/* Left Column */}
            <div>
                <div className="flex items-center">
                    <div className="w-4 h-4 rounded-full bg-primaryBlue flex-shrink-0"></div>
                    <h6 className="text-md font-medium text-white ltr:ml-2 rtl:mr-2">{t('contact.subtitle')}</h6>
                </div>
                <h2 className="text-4xl font-semibold mt-5 text-white whitespace-pre-line">
                  {t('contact.title')}
                </h2>
            </div>
            
            {/* Right Column - Contact Information */}
            <div className="pb-8 border-b border-white/20">
              <div className="space-y-8">
                {/* Phone Information */}
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-primaryBlue/20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primaryBlue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 8V5z" />
                    </svg>
                  </div>
                  <div className="ltr:ml-4 rtl:mr-4">
                    <h3 className="text-lg font-medium text-white">{t('contact.info.phone.title')}</h3>
                    <p className="mt-1 text-gray-300">
                      <a href="tel:+902622060260" className="hover:text-primaryBlue transition-colors duration-300">
                        0262 206 02 60
                      </a>
                    </p>
                  </div>
                </div>
                
                {/* Address Information */}
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-primaryBlue/20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primaryBlue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="ltr:ml-4 rtl:mr-4">
                    <h3 className="text-lg font-medium text-white">{t('contact.info.address.title')}</h3>
                    <p className="mt-1 text-gray-300 max-w-xs">
                      Balçık Mah, 3227 Nolu Sokak No:9, Gebze / Kocaeli
                    </p>
                  </div>
                </div>
              </div>
            </div>
            </div>

            {/* Contact Card */}
            <div className="bg-primaryNavy backdrop-blur-sm rounded-2xl p-8 shadow-xl">
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Name Input */}
                        <div>
                            <label htmlFor="user_name" className="block text-sm font-medium text-gray-300">
                              {t('contact.form.name.label')}
                            </label>
                            <input
                              type="text"
                              name="user_name"
                              id="user_name"
                              required
                              className="mt-1 block py-3 px-3 w-full rounded-lg border-gray-700 bg-white text-black shadow-sm focus:border-primaryBlue focus:ring-primaryBlue"
                              placeholder={t('contact.form.name.placeholder')}
                            />
                        </div>

                        {/* Email Input */}
                        <div>
                            <label htmlFor="user_email" className="block text-sm font-medium text-gray-300">
                              {t('contact.form.email.label')}
                            </label>
                            <input
                              type="email"
                              name="user_email"
                              id="user_email"
                              required
                              className="mt-1 block py-3 px-3 w-full rounded-lg border-gray-700 bg-white text-black shadow-sm focus:border-primaryBlue focus:ring-primaryBlue"
                              placeholder={t('contact.form.email.placeholder')}
                            />
                        </div>
                    </div>

                    {/* Message Input */}
                    <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-300">
                          {t('contact.form.message.label')}
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          rows={4}
                          className="mt-1 block py-3 px-3 w-full rounded-lg border-gray-700 bg-white text-black shadow-sm focus:border-primaryBlue focus:ring-primaryBlue"
                          placeholder={t('contact.form.message.placeholder')}
                        />
                    </div>

                    {/* Error and Success Messages */}
                    {error && (
                      <div className="text-red-500 text-sm">{error}</div>
                    )}
                    {success && (
                      <div className="text-green-500 text-sm">{t('contact.form.success')}</div>
                    )}

                    {/* Submit Button */}
                    <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={loading}
                          className="group relative bg-white text-gray-900 rounded-full font-medium transition-all duration-300 overflow-hidden hover:bg-primaryBlue hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <div className="grid grid-cols-[1fr,3rem] items-center w-full">
                            <span className="px-8 py-3 transition-transform duration-300 group-hover:translate-x-4">
                              {loading ? t('contact.form.sending') : t('contact.form.submit')}
                            </span>
                            <div className="w-6 h-6 rounded-full bg-primaryBlue group-hover:bg-white flex items-center justify-center transform transition-all duration-300 group-hover:-translate-x-2">
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                className="h-4 w-4 text-white group-hover:text-primaryBlue" 
                                viewBox="0 0 20 20" 
                                fill="currentColor"
                              >
                                <path 
                                  fillRule="evenodd" 
                                  d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" 
                                  clipRule="evenodd" 
                                />
                              </svg>
                            </div>
                          </div>
                        </button>
                    </div>
                </form>
            </div>
        </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact; 